import { useQueryState } from 'nuqs'
import { useEffect } from 'react'
import {
  getSfSearchProducts,
  TsfSearchProducts,
} from '~/lib/shopify/product/search-products'

import {
  UnlikelySearchProductSortKeys,
  UnlikelySearchUnavailableProductsType,
} from '@unlikelystudio/commerce-connector'
import { useSearch } from '@unlikelystudio/react-ecommerce-hooks'

import { SEARCH_HITS_PER_PAGE } from '~/components/Slices/Search'

import { useShopifySearchContext } from '~/providers/ShopifySearchProvider'

import useLocale from '~/hooks/useLocale'

import { getShopifyProductMetafieldsList } from '~/data/product-page-data/serialize-product-metafields'
import { serializeCardProductContent } from '~/data/serialize-card-product'

export interface UseShopifySearchProps {
  query?: string
  filters?: TsfSearchProducts['productFilters']
  enabled?: boolean
  key: string
  hitsPerPage?: number
}

export function useShopifySearch(props?: UseShopifySearchProps) {
  const { queryKey, query, hitsPerPage, initialData } =
    useShopifySearchContext()
  const [_, setSearchParam] = useQueryState('q')

  const locale = useLocale()
  const processedQuery = typeof props?.query === 'string' ? props.query : query

  useEffect(() => {
    if (processedQuery) {
      setSearchParam(processedQuery)
    } else {
      setSearchParam(null)
    }
  }, [processedQuery])

  return useSearch({
    key: props?.key ?? queryKey,
    query: processedQuery,
    paginationOptions: {
      hitsPerPage: hitsPerPage ?? SEARCH_HITS_PER_PAGE,
      getNextPageParam: (lastPage) => {
        return lastPage?.pageInfo.hasNextPage
          ? { after: lastPage.pageInfo.endCursor }
          : undefined
      },
      getPreviousPageParam: (lastPage) => {
        return lastPage?.pageInfo.hasPreviousPage
          ? { before: lastPage.pageInfo.startCursor }
          : undefined
      },
    },
    queryOptions: {
      enabled: props?.enabled ?? true,
      refetchOnMount: true,
      initialData: processedQuery
        ? {
            pages: [initialData],
            pageParams: [],
          }
        : undefined,
      keepPreviousData: processedQuery.length > 0,
    },
    searchFunction: async ({ pageParam }) => {
      const searchProducts = await getSfSearchProducts({
        query: processedQuery,
        locale,
        first: hitsPerPage ?? SEARCH_HITS_PER_PAGE,
        ...(pageParam?.after || pageParam?.before ? pageParam : {}),
        includePrices: true,
        imagesFirst: 100,
        variantsFirst: 20,
        productFilters: [...(props?.filters ?? [])],
        sortKey: UnlikelySearchProductSortKeys.Relevance,
        unavailableProducts: UnlikelySearchUnavailableProductsType.Last,
        metafieldKeys: getShopifyProductMetafieldsList('productCard'),
        includeSelectedOptions: true,
      })

      const serializedProductCards = await Promise.all(
        searchProducts?.results
          ?.filter(Boolean)
          ?.map(
            async (product) =>
              await serializeCardProductContent(product, locale),
          ) ?? [],
      )

      return {
        ...searchProducts,
        serializedProductCards,
      }
    },
  })
}
